import { enqueueSnackbar } from 'notistack';
import { useRef, useState, Activity } from 'react';

import {
  IconEdit,
  IconTrash,
  IconHistory,
  IconFileExport,
} from '@tabler/icons-react';

import { Breadcrumb } from '@/components/BreadCrumbs';
import ConfirmationModal from '@/components/ConfirmationModal';
import CustomModal from '@/components/CustomModal';
import {
  ERoleAccess,
  ESchoolStatus,
  ERestoreEntityType,
  ESubscriptionStatus,
} from '@/enum';
import useAccess from '@/hooks/useAccess';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import { useFormatText } from '@/hooks/useFormatText';
import { softdeleteRecord } from '@/service/commonService';
import useCommonStore from '@/store/useCommonStore';
import { ISchoolDetails, ISelectedRowValue } from '@/types';

import {
  EditSubscriptionModal,
  type IEditSubscriptionModalHandle,
} from './EditSubscriptionModal';
import {
  ScheduleRenewalModal,
  type IScheduleRenewalModalHandle,
} from './ScheduleRenewalModal';
import { ViewSchoolDetailsModal } from './ViewSchoolDetailsModal';

type TStep = 'details' | 'schedule' | 'edit';

const toExpiryMs = (value: ISchoolDetails['expiry_date']): number | null => {
  if (!value) return null;
  const num = Number(value);
  if (Number.isFinite(num)) {
    return num.toString().length === 10 ? num * 1000 : num;
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
};

interface ISchoolDetailsModalProps {
  setIsOpen: (open: boolean) => void;
  selectedSchool: ISelectedRowValue | null;
  onRemoveSubmit: () => void;
  onSubscriptionUpdate?: () => void;
}

export const SchoolDetailsModal = ({
  setIsOpen,
  selectedSchool,
  onRemoveSubmit,
  onSubscriptionUpdate,
}: ISchoolDetailsModalProps) => {
  const { hasAccess } = useAccess();
  const { formatDate } = useDateFormatter();

  const [step, setStep] = useState<TStep>('details');
  const [isRemoveSchoolModalOpen, setIsRemoveSchoolModalOpen] = useState(false);
  const [confirmationLoading, setConfirmationLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [school, setSchool] = useState<ISchoolDetails | null>(null);
  const { formatText } = useFormatText();
  const { loadingModal } = useCommonStore();

  const scheduleRenewalRef = useRef<IScheduleRenewalModalHandle>(null);
  const editSubscriptionRef = useRef<IEditSubscriptionModalHandle>(null);

  const expiryTimestamp = school?.expiry_date;
  const expiryMs = toExpiryMs(expiryTimestamp);
  const expiryDateLabel = expiryTimestamp ? formatDate(expiryTimestamp) : '-';
  const expiryDateIso = expiryMs
    ? new Date(expiryMs).toISOString().split('T')[0]
    : undefined;

  const shouldShowRenewal =
    school?.subscription_status !== undefined &&
    school.subscription_status !== ESubscriptionStatus.ACTIVE;
  const isUpcoming =
    school?.subscription_status === ESubscriptionStatus.UPCOMING;
  const isCancelledNotExpired =
    school?.subscription_status === ESubscriptionStatus.CANCELLED &&
    expiryMs !== null &&
    expiryMs > Date.now();

  const getBreadcrumbPath = (current: TStep) => {
    const base = [
      { label: formatText('school.school-details'), href: 'details' },
    ];
    if (current === 'schedule') {
      return [
        ...base,
        {
          label: formatText('school.actions.schedule-renewal'),
          href: 'schedule',
        },
      ];
    }
    if (current === 'edit') {
      return [
        ...base,
        {
          label: formatText('school.actions.edit-subscription'),
          href: 'edit',
        },
      ];
    }
    return base;
  };

  // Handlers
  const handleCancel = () => {
    if (step === 'schedule' || step === 'edit') {
      setIsOpen(false);
    } else {
      setIsRemoveSchoolModalOpen(true);
    }
  };

  const handleConfirm = async () => {
    if (step === 'details') {
      if (school?.status === ESchoolStatus.IN_ACTIVE) {
        enqueueSnackbar(formatText('school.actions.activate-school-first'), {
          variant: 'warning',
        });
        return;
      }
      if (isCancelledNotExpired) {
        enqueueSnackbar(
          formatText('school.actions.cancel-renewal-not-allowed'),
          { variant: 'warning' }
        );
        return;
      }
      setStep(shouldShowRenewal ? 'schedule' : 'edit');
      return;
    }
    const success =
      step === 'schedule'
        ? await scheduleRenewalRef.current?.submit()
        : await editSubscriptionRef.current?.submit();
    if (success) {
      onSubscriptionUpdate?.();
      setIsOpen(false);
    }
  };

  const handleRemoveSchool = async () => {
    if (!selectedSchool) return;
    setConfirmationLoading(true);
    try {
      const params = {
        id: selectedSchool.id.toString(),
        record_type: ERestoreEntityType.SCHOOL,
      };
      const response = await softdeleteRecord(params);
      if (response?.status) {
        onRemoveSubmit();
      } else {
        console.error(response?.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setConfirmationLoading(false);
      setIsRemoveSchoolModalOpen(false);
    }
  };

  // Button props
  const isOnFormStep = step === 'schedule' || step === 'edit';
  const detailsActionLabel = shouldShowRenewal
    ? formatText('school.actions.schedule-renewal')
    : formatText('school.actions.edit-subscription');
  const formActionLabel =
    step === 'schedule'
      ? formatText('school.actions.schedule-renewal')
      : formatText('school.actions.edit-subscription');

  const cancelButtonProps = {
    label: isOnFormStep
      ? formatText('common.actions.cancel')
      : formatText('school.actions.remove-school'),
    icon: isOnFormStep ? undefined : <IconTrash size={16} />,
    className: !isOnFormStep ? '!text-red-600' : 'justify-start!',
    desructive: true,
    onClick: handleCancel,
    disabled: loadingModal,
  };

  const detailsActionIcon = shouldShowRenewal ? (
    <IconFileExport size={16} />
  ) : (
    <IconEdit size={16} />
  );

  const confirmButtonProps =
    isUpcoming && !isOnFormStep
      ? undefined
      : {
          label: isOnFormStep ? formActionLabel : detailsActionLabel,
          variant: 'primary' as const,
          icon: isOnFormStep ? undefined : detailsActionIcon,
          onClick: handleConfirm,
          disabled: loadingModal || actionLoading,
          loading: actionLoading,
        };

  if (!hasAccess(ERoleAccess.USER_MANAGEMENT)) return null;

  return (
    <>
      <CustomModal
        setIsOpen={setIsOpen}
        isHeader
        isFooter
        loadingModal={loadingModal}
        position="right"
        headercontent={
          step === 'details' ? (
            <h2 className="text-lg leading-7">
              {formatText('school.school-details')}
            </h2>
          ) : (
            <Breadcrumb
              path={getBreadcrumbPath(step)}
              onNavigateRequest={(href) => {
                if (href === 'details') setStep('details');
              }}
            />
          )
        }
        cancelButtonProps={cancelButtonProps}
        confirmButtonProps={confirmButtonProps}
      >
        {step === 'details' && (
          <div className="flex min-h-full w-full flex-col gap-4">
            {isUpcoming && (
              <div className="bg-warning-light-50 border border-warning-light-100 rounded-xl p-4 flex items-center gap-3">
                <IconHistory className="text-warning-secondary" size={24} />
                <span className="text-base font-semibold text-neutral-primary">
                  {formatText('school.actions.renewal-already-scheduled')}
                </span>
              </div>
            )}
            <ViewSchoolDetailsModal
              schoolId={selectedSchool?.id}
              onSchoolLoaded={setSchool}
            />
          </div>
        )}
        {step === 'schedule' && (
          <ScheduleRenewalModal
            ref={scheduleRenewalRef}
            schoolId={selectedSchool?.id?.toString()}
            schoolName={school?.name}
            expiryDate={expiryDateLabel}
            expiryDateIso={expiryDateIso}
            onLoadingChange={setActionLoading}
          />
        )}
        {step === 'edit' && (
          <EditSubscriptionModal
            ref={editSubscriptionRef}
            schoolId={selectedSchool?.id?.toString()}
            onLoadingChange={setActionLoading}
          />
        )}
      </CustomModal>
      <Activity mode={isRemoveSchoolModalOpen ? 'visible' : 'hidden'}>
        <ConfirmationModal
          isOpen={isRemoveSchoolModalOpen}
          setIsOpen={setIsRemoveSchoolModalOpen}
          title="school.actions.remove-school-title"
          descriptionPrefix="school.actions.remove-school-prefix"
          descriptionSuffix="school.actions.remove-school-suffix"
          entityName={selectedSchool?.name}
          confirmLabel={formatText('school.actions.remove-school')}
          onConfirm={handleRemoveSchool}
          isLoading={confirmationLoading}
          isDestructive
        />
      </Activity>
    </>
  );
};
